import { useNavigate } from 'react-router-dom'
import { Search, Users } from 'lucide-react'
import { usePatientSearch } from '@/hooks/usePatientSearch'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate } from '@/lib/dates'
import { formatConditionCategory } from '@/lib/format'
import ErrorState from '@/components/shared/ErrorState'
import EmptyState from '@/components/shared/EmptyState'

export default function PatientSearchPage() {
  const navigate = useNavigate()
  const { query, setQuery, results, isLoading, error, refetch } = usePatientSearch()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Patients</h1>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search patients by name..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search patients"
          className="pl-9"
        />
      </div>

      {error && (
        <ErrorState message="Failed to load patients." onRetry={() => refetch()} />
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      )}

      {!isLoading && !error && results && results.length === 0 && (
        <EmptyState
          icon={Users}
          title={query.length >= 2 ? `No patients found for '${query}'` : 'No patients yet'}
        />
      )}

      {!isLoading && results && results.length > 0 && (
        <div className="space-y-2">
          {results.map((patient) => (
            <button
              key={patient.id}
              type="button"
              className="flex w-full items-center justify-between rounded-lg border bg-white p-4 text-left hover:bg-slate-50 transition-colors"
              onClick={() => navigate(`/patients/${patient.id}`)}
            >
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-slate-900">
                  {patient.first_name} {patient.last_name}
                </span>
                <span className="text-xs text-slate-500">
                  DOB: {formatDate(patient.date_of_birth)}
                  {patient.last_visit_date && (
                    <> &middot; Last visit: {formatDate(patient.last_visit_date)}</>
                  )}
                </span>
              </div>
              {patient.last_condition_category && (
                <Badge variant="secondary">
                  {formatConditionCategory(patient.last_condition_category)}
                </Badge>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
